/* eslint-disable max-len */
import { FC, useCallback, useState } from 'react';
import { AllieChip, AllieChipColorProps } from '../../../components';
import { AllieColors } from '../../../common/color.types';
import {
  AllieAlertCircleOutlineIcon,
  AllieCheckmarkOutlineIcon,
  AllieChevronsUpIcon,
  AllieCloseCircleOutlineIcon,
  AllieDisconnectedIcon,
  AllieSettingIcon,
} from '../../../styling/icons';
import {
  ItemWrapper,
  MachineLastUpdateText,
  MachineNameText,
  MachineStatusText,
  MachineVariableStatusText,
  MachineVariableText,
  MachineVariableValue,
  SnakeArrow,
  SnakeContent,
  StatusIcon,
  VariableConfig,
} from './SnakeContainer.styles';
import { MachineStatusType, SnakeItemProps } from './SnakeContainer.types';
import VariableConfigModal from './VariableConfigModal';

enum MachineStatus {
  normal = 'normal',
  warning = 'warning',
  alert = 'alert',
  neutral = 'neutral',
  no_production = 'no_production',
}

const mapStatusColorText: Record<string, AllieColors | null> = {
  alert: 'error-main',
  normal: 'success-main',
  neutral: 'neutral-50',
  warning: 'warning-main',
};

const mapStationStatusChipColor: Record<string, string | undefined> = {
  alert: 'var(--allie-color-error-5)',
  warning: 'var(--allie-color-warning-5)',
  neutral: 'var(--allie-color-neutral-40)',
};
const mapStationStatusChipBorder: Record<string, string> = {
  alert: '1px solid var(--allie-color-error-20)',
  warning: '1px solid var(--allie-color-warning-20)',
  neutral: '1px solid var(--allie-color-neutral-40)',
};

export const SnakeItem: FC<SnakeItemProps> = ({
  configVarLabel,
  arrangedObjects,
  item,
  itemIndex,
  row,
  rowIndex,
  statesFilter,
  onItemClick,
  isStationActive,
  isMobile,
  onUpdateVariableDisplay,
  loadingUpdateVariableDisplay,
  showVariableConfig,
}) => {
  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
  const [mutationSuccess, setMutationSuccess] = useState<boolean | null>(null);

  const handleConfigClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setIsConfigModalOpen(true);
    setMutationSuccess(null);
  }, []);

  const handleSaveConfig = useCallback(
    async (updatedVariables: any[]): Promise<void> => {
      if (onUpdateVariableDisplay) {
        const machineVariableIds = updatedVariables
          .filter((v) => v.isCustomDisplay)
          .map((v) => v.id);
        const success = await onUpdateVariableDisplay(item.id, machineVariableIds);
        setMutationSuccess(success);
      } else {
        setMutationSuccess(false);
      }
    },
    [onUpdateVariableDisplay, item.id]
  );

  const handleCloseModal = useCallback(() => {
    setIsConfigModalOpen(false);
    setMutationSuccess(null);
  }, []);

  const getStatusIcon = (status: MachineStatusType) => {
    switch (status) {
      case MachineStatus.alert:
        return (
          <StatusIcon
            background={
              statesFilter[MachineStatus.alert]
                ? 'var(--allie-color-error-5)'
                : 'var(--allie-color-neutral-5)'
            }
            color={
              statesFilter[MachineStatus.alert]
                ? 'var(--allie-color-error-60)'
                : 'var(--allie-color-neutral-40)'
            }
          >
            <AllieAlertCircleOutlineIcon />
          </StatusIcon>
        );
      case MachineStatus.warning:
        return (
          <StatusIcon
            background={
              statesFilter[MachineStatus.warning]
                ? 'var(--allie-color-warning-5)'
                : 'var(--allie-color-neutral-5)'
            }
            color={
              statesFilter[MachineStatus.warning]
                ? 'var(--allie-color-warning-main)'
                : 'var(--allie-color-neutral-40)'
            }
          >
            <AllieAlertCircleOutlineIcon />
          </StatusIcon>
        );
      case MachineStatus.normal:
        return (
          <StatusIcon
            background={
              statesFilter[MachineStatus.normal]
                ? 'var(--allie-color-success-5)'
                : 'var(--allie-color-neutral-5)'
            }
            color={
              statesFilter[MachineStatus.normal]
                ? 'var(--allie-color-success-60)'
                : 'var(--allie-color-neutral-40)'
            }
          >
            <AllieCheckmarkOutlineIcon />
          </StatusIcon>
        );
      case MachineStatus.no_production:
        return (
          <StatusIcon
            background={'var(--allie-color-neutral-5)'}
            color={'var(--allie-color-neutral-50)'}
          >
            <AllieCloseCircleOutlineIcon />
          </StatusIcon>
        );

      default:
        return (
          <StatusIcon
            background="var(--allie-color-neutral-40)"
            color="var(--allie-color-light-main)"
          >
            <AllieDisconnectedIcon />
          </StatusIcon>
        );
    }
  };

  const isEmpty = Object.keys(item).length === 0;

  const getFilteredVariables = useCallback((variables: any[]) => {
    const customDisplayVariables = variables.filter((v) => v.isCustomDisplay);
    const otherVariables = variables.filter((v) => !v.isCustomDisplay);

    if (customDisplayVariables.length >= 3) {
      return customDisplayVariables.slice(0, 3);
    } else {
      return [
        ...customDisplayVariables,
        ...otherVariables.slice(0, 3 - customDisplayVariables.length),
      ];
    }
  }, []);

  const isLastItem = useCallback(() => {
    const isLastRow = rowIndex === arrangedObjects.length - 1;
    const isLastItemInRow = itemIndex === row.length - 1;
    const isOddRow = rowIndex % 2 !== 0;

    if (isLastRow) {
      return isOddRow ? itemIndex === 0 : isLastItemInRow;
    }

    return false;
  }, [rowIndex, itemIndex, arrangedObjects.length, row.length]);

  return (
    <ItemWrapper isLastItem={isLastItem()} isEmpty={isEmpty} isMobile={isMobile}>
      <SnakeContent
        style={{ ...(onItemClick && !isEmpty ? { cursor: 'pointer' } : {}) }}
        isEmpty={isEmpty}
        status={item.status}
        stateFilter={statesFilter[item.status as MachineStatus]}
        isStationActive={isStationActive}
        isMobile={isMobile}
        content={{
          children: (
            <div
              style={{
                display: !isEmpty ? 'block' : 'none',
                alignItems: 'center',
                gap: 'var(--allie-spacing-md)',
              }}
            >
              <div className="machineName" style={{ width: '100%' }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    height: 36,
                    marginBottom: 4,
                  }}
                >
                  <MachineNameText
                    className="machineName"
                    color={statesFilter[item.status as MachineStatus] ? 'primary-90' : 'neutral-40'}
                    isMobile={isMobile}
                  >
                    {item.machineName}
                  </MachineNameText>
                  {showVariableConfig && (
                    <VariableConfig
                      background="var(--allie-color-neutral-5)"
                      onClick={handleConfigClick}
                      style={{
                        cursor: 'pointer',
                      }}
                    >
                      <AllieSettingIcon />
                    </VariableConfig>
                  )}
                </div>
                <div style={{ marginBottom: 8 }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      borderBottom: '1px solid var(--allie-color-neutral-20)',
                      borderTop: '1px solid var(--allie-color-neutral-20)',
                      marginBottom: 4,
                      padding: '2px 0',
                    }}
                  >
                    <div style={{ display: 'flex', gap: 'var(--allie-spacing-xs)' }}>
                      {getStatusIcon(item.status)}
                      <MachineStatusText
                        color={
                          statesFilter[item.status as MachineStatus]
                            ? (mapStatusColorText[item.status] as AllieColors | undefined)
                            : (mapStatusColorText[MachineStatus.neutral] as AllieColors | undefined)
                        }
                        weight={700}
                        isMobile={isMobile}
                      >
                        {item.statusText}
                      </MachineStatusText>
                    </div>
                    {item.lastUpdate && (
                      <MachineLastUpdateText color="neutral-60" isMobile={isMobile}>
                        {item.lastUpdate}
                      </MachineLastUpdateText>
                    )}
                  </div>

                  {item.variables &&
                    getFilteredVariables(item.variables).map(
                      (
                        variable: {
                          status: MachineStatus;
                          name: string;
                          currentValue: string;
                          isCustomDisplay: boolean;
                        },
                        index: number
                      ) => (
                        <div
                          key={variable.name + index}
                          style={{ display: 'flex', justifyContent: 'space-between' }}
                        >
                          <MachineVariableText isMobile={isMobile} weight={600}>
                            {variable.name}
                          </MachineVariableText>
                          <MachineVariableValue
                            isMobile={isMobile}
                            weight={600}
                            color={mapStatusColorText[variable.status] as AllieColors | undefined}
                          >
                            {variable.currentValue}
                          </MachineVariableValue>
                        </div>
                      )
                    )}
                  {item.variablesStatus && (
                    <AllieChip
                      color={'var(--allie-color-neutral-40)' as AllieChipColorProps | undefined}
                      style={{
                        height: 14,
                        margin: '6px 0 0',
                        padding: '0px 10px',
                        border: mapStationStatusChipBorder['neutral'],
                        ['borderRadius']: '9999px',
                        cursor: onItemClick ? 'pointer' : 'default',
                        background: 'var(--allie-color-neutral-5)',
                      }}
                    >
                      <MachineVariableStatusText color="neutral-70" isMobile={isMobile}>
                        {item.variablesStatusText}
                      </MachineVariableStatusText>
                    </AllieChip>
                  )}
                </div>
              </div>
            </div>
          ),
        }}
        onClick={() => !isEmpty && onItemClick?.(item)}
      />
      {(rowIndex !== itemIndex || isMobile) &&
      itemIndex === row.length - 1 &&
      rowIndex % 2 === 0 ? (
        <SnakeArrow className="down" isLastItem={isLastItem()} isEmpty={isEmpty}>
          <AllieChevronsUpIcon />
        </SnakeArrow>
      ) : rowIndex % 2 !== 0 && itemIndex === 0 ? (
        <SnakeArrow className="down" isLastItem={isLastItem()} isEmpty={isEmpty}>
          <AllieChevronsUpIcon />
        </SnakeArrow>
      ) : (
        <SnakeArrow
          className={rowIndex % 2 === 0 ? 'right' : 'left'}
          isLastItem={
            arrangedObjects.length === rowIndex + 1 &&
            ((row[itemIndex - 1] && Object.keys(row[itemIndex - 1]).length === 0) ||
              (row[itemIndex + 1] && Object.keys(row[itemIndex + 1]).length === 0))
          }
          isEmpty={isEmpty}
        >
          <AllieChevronsUpIcon />
        </SnakeArrow>
      )}
      <VariableConfigModal
        configVarLabel={configVarLabel}
        isOpen={isConfigModalOpen}
        onClose={handleCloseModal}
        machineName={item.machineName}
        variables={item.variables}
        onSave={handleSaveConfig}
        loadingUpdateVariableDisplay={loadingUpdateVariableDisplay}
        mutationSuccess={mutationSuccess}
        isMobile={isMobile}
      />
    </ItemWrapper>
  );
};
